import { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { useToast } from "@/hooks/use-toast";
import { Eye, EyeOff, Loader2, ArrowLeft, CheckCircle2, Check, X, AlertCircle, Mail } from "lucide-react";
import { signInSchema, signUpSchema } from "@/lib/validation/authSchemas";
import { PasswordStrengthIndicator } from "@/components/PasswordStrengthIndicator";

import { ForgotPasswordDialog } from "@/components/auth/ForgotPasswordDialog";
import { logger } from "@/utils/logger";

import { hapticSuccess, hapticError } from "@/utils/haptics";
import { isMobileDevice } from "@/utils/mobileOptimizations";
import Logo from "@/components/Logo";
import { useServerConversion } from "@/hooks/useServerConversion";

const Auth = () => {
  const [searchParams] = useSearchParams();
  const redirectPath = searchParams.get('redirect');
  const [isSignUp, setIsSignUp] = useState(searchParams.get('mode') === 'signup');
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [forgotPasswordOpen, setForgotPasswordOpen] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [isEmailValid, setIsEmailValid] = useState(false);
  const [errors, setErrors] = useState<{ email?: string; password?: string }>({});
  const [emailVerificationPending, setEmailVerificationPending] = useState(false);
  const [accountRecoveryPending, setAccountRecoveryPending] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [initialLoading, setInitialLoading] = useState(true);
  const navigate = useNavigate();
  const { toast } = useToast();
  const errorAnnouncementRef = useRef<HTMLDivElement>(null);
  
  // Server-side conversion tracking (cookieless)
  const { 
    trackSignupPageView, 
    trackSignupStarted, 
    trackSignupCompleted,
    getConversionSessionId 
  } = useServerConversion();

  // Password validation requirements
  // Simplified password requirements for mobile-friendly signup
  const requirements = [
    { label: "At least 8 characters", test: (pwd: string) => pwd.length >= 8 },
    { label: "Contains a letter", test: (pwd: string) => /[a-zA-Z]/.test(pwd) },
    { label: "Contains a number", test: (pwd: string) => /\d/.test(pwd) },
  ];

  const getRequirementStatus = (requirement: { test: (pwd: string) => boolean }) => {
    if (!password) return null;
    return requirement.test(password);
  };

  // Track signup page view (cookieless conversion tracking)
  useEffect(() => {
    if (isSignUp) {
      trackSignupPageView();
    }
  }, [isSignUp, trackSignupPageView]);

  // Redirect already authenticated users to dashboard
  useEffect(() => {
    const redirectIfAuthenticated = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          navigate("/dashboard");
        }
      } finally {
        setInitialLoading(false);
      }
    };

    redirectIfAuthenticated();
  }, [navigate]);

  // Validate email in real-time
  useEffect(() => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    setIsEmailValid(emailRegex.test(email.trim()));
  }, [email]);

  const handleAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});

    // Blur active input to close mobile keyboard
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }

    // Auto-scroll to top on mobile to show any errors
    if (window.innerWidth < 1024) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }

    // Validate form
    const schema = isSignUp ? signUpSchema : signInSchema;
    const validation = schema.safeParse({ email, password });

    if (!validation.success) {
      const fieldErrors: any = {};
      validation.error.errors.forEach((err) => {
        if (err.path[0]) {
          fieldErrors[err.path[0]] = err.message;
        }
      });
      setErrors(fieldErrors);
      return;
    }

    setLoading(true);

    try {
      if (isSignUp) {
        // Track signup started (cookieless conversion tracking)
        const emailDomain = email.trim().split('@')[1];
        trackSignupStarted(emailDomain);

        const { error, data } = await supabase.auth.signUp({
          email: email.trim(),
          password,
          options: {
            emailRedirectTo: `${window.location.origin}/auth`,
          },
        });

        if (error) {
          if (error.message.includes("already registered")) {
            // Trigger password reset for existing account instead of showing error
            await supabase.auth.resetPasswordForEmail(email.trim(), {
              redirectTo: `${window.location.origin}/reset-password`,
            });
            setAccountRecoveryPending(true);
            hapticSuccess();
            toast({
              title: "Account found!",
              description: "We've sent you a password reset link.",
            });
            setLoading(false);
            return;
          }
          throw error;
        }

        // Check if email confirmation is required
        if (data.user && !data.user.email_confirmed_at) {
          // Email verification is required
          setEmailVerificationPending(true);
          hapticSuccess();
          toast({
            title: "Verification email sent!",
            description: "Please check your inbox and spam folder.",
          });
          return;
        }

        // If email is already confirmed (auto-confirm enabled), proceed with session
        let sessionInitialized = false;
        let retries = 0;
        const maxRetries = 5;
        
        while (!sessionInitialized && retries < maxRetries) {
          await new Promise(resolve => setTimeout(resolve, 500));
          
          const { data: { session }, error: sessionError } = await supabase.auth.getSession();
          
          if (sessionError) {
            logger.error("Session error during initialization", sessionError);
            retries++;
            continue;
          }
          
          if (session) {
            sessionInitialized = true;
            break;
          }
          
          retries++;
        }
        
        if (!sessionInitialized) {
          logger.error("Failed to initialize session after signup");
          toast({
            title: "Session Error",
            description: "Please try logging in again.",
            variant: "destructive"
          });
          return;
        }
        
        // Send welcome email (fire and forget)
        supabase.functions.invoke('send-welcome-email').catch((err) => {
          logger.error("Failed to send welcome email", err);
        });
        
        // Track signup completed and link session to user (cookieless conversion tracking)
        if (data.user) {
          trackSignupCompleted(data.user.id);
          // Link all previous conversion events to this user
          getConversionSessionId().then(async (sessionId) => {
            try {
              await supabase.rpc('link_conversion_session_to_user', {
                p_session_id: sessionId,
                p_user_id: data.user!.id
              });
            } catch (err) {
              logger.error("Failed to link conversion session", err);
            }
          });
        }
        
        hapticSuccess();
        toast({
          title: "Account created!",
          description: "Welcome to SendFeedback!",
        });
        navigate("/dashboard");
      } else {
        const { error } = await supabase.auth.signInWithPassword({
          email: email.trim(),
          password,
        });
        
        // If remember me is enabled, update session to persist longer
        if (!error && rememberMe) {
          await supabase.auth.refreshSession();
        }

        if (error) {
          // Check for actual unverified email error
          if (error.message.includes("Email not confirmed")) {
            setEmailVerificationPending(true);
            toast({
              title: "Email not verified",
              description: "Please verify your email before signing in.",
            });
            setLoading(false);
            return;
          }
          
          // Show generic error for invalid credentials
          if (error.message.includes("Invalid login credentials")) {
            throw new Error("Invalid email or password. Please check your credentials and try again.");
          }
          
          throw error;
        }

        hapticSuccess();
        toast({
          title: "Welcome back!",
          description: "You've successfully signed in.",
        });
        navigate(redirectPath || "/dashboard");
      }
    } catch (error: unknown) {
      hapticError();
      const errorMessage = error instanceof Error ? error.message : "Authentication failed";
      toast({
        title: "Authentication failed",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };


  const toggleMode = () => {
    const newMode = !isSignUp;
    setIsSignUp(newMode);
    setErrors({});
    setPassword("");
    setRememberMe(false);
    setEmailVerificationPending(false);
    setAccountRecoveryPending(false);
    
    // Update URL parameter
    navigate(`/auth?mode=${newMode ? 'signup' : 'signin'}`, { replace: true });
  };

  const handleResendPasswordReset = async () => {
    if (resendCooldown > 0) return;

    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email.trim(), {
        redirectTo: `${window.location.origin}/reset-password`,
      });

      if (error) throw error;

      toast({
        title: "Email resent!",
        description: "Check your inbox for the password reset link.",
      });

      // Start 60-second cooldown
      setResendCooldown(60);
      const interval = setInterval(() => {
        setResendCooldown((prev) => {
          if (prev <= 1) {
            clearInterval(interval);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : "Failed to resend email";
      toast({
        title: "Failed to resend email",
        description: errorMessage,
        variant: "destructive",
      });
    }
  };

  const handleResendVerification = async () => {
    if (resendCooldown > 0) return;

    try {
      const { error } = await supabase.auth.resend({
        type: 'signup',
        email: email.trim(),
      });

      if (error) throw error;

      toast({
        title: "Email resent!",
        description: "Check your inbox for the verification link.",
      });

      // Start 60-second cooldown
      setResendCooldown(60);
      const interval = setInterval(() => {
        setResendCooldown((prev) => {
          if (prev <= 1) {
            clearInterval(interval);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : "Failed to resend email";
      toast({
        title: "Failed to resend email",
        description: errorMessage,
        variant: "destructive",
      });
    }
  };


  if (initialLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col lg:flex-row relative overflow-hidden">
      {/* Atrium ambient sun-glow backdrop */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 -z-0 opacity-70"
        style={{
          background:
            "radial-gradient(900px 520px at 0% 0%, hsl(50 60% 92% / 0.6), transparent 60%), radial-gradient(800px 500px at 100% 100%, hsl(150 30% 90% / 0.45), transparent 60%)",
        }}
      />

      {/* Left side - Atrium hero column (desktop only) */}
      <aside className="hidden lg:flex lg:w-[45%] xl:w-[42%] flex-col justify-between p-12 xl:p-16 relative">
        <div className="relative z-10 flex items-center justify-between">
          <Logo size="sm" />
          <button
            onClick={() => navigate("/")}
            className="inline-flex items-center gap-2 rounded-full border border-foreground/10 bg-background/70 backdrop-blur px-4 py-2 text-xs font-medium text-foreground/70 hover:bg-foreground hover:text-background hover:border-foreground transition-all"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            Back home
          </button>
        </div>

        {/* Headline */}
        <div className="relative z-10 max-w-lg space-y-7">
          <span className="inline-flex items-center gap-2 rounded-full border border-border/60 bg-background/70 backdrop-blur px-3.5 py-1.5 text-[11px] uppercase tracking-[0.22em] font-medium text-foreground/70 shadow-[0_2px_8px_-4px_rgba(21,34,27,0.06)]">
            <span className="h-1.5 w-1.5 rounded-full bg-primary animate-pulse" />
            The honest channel
          </span>
          <h2 className="font-sans text-5xl xl:text-[60px] leading-[1.02] tracking-[-0.025em] font-medium text-foreground">
            Honest feedback,{" "}
            <span className="italic font-light text-foreground/75">on the record.</span>
          </h2>
          <p className="font-sans text-lg text-muted-foreground leading-[1.6]">
            Sign in to read the latest, or open a new account to start
            collecting fully anonymous feedback from the people who matter most.
          </p>
        </div>

        {/* Three pillars */}
        <div className="relative z-10 grid grid-cols-3 gap-3">
          {[
            { n: "01", label: "Anonymous" },
            { n: "02", label: "Unfiltered" },
            { n: "03", label: "Actionable" },
          ].map((item) => (
            <div
              key={item.n}
              className="rounded-2xl bg-card/80 backdrop-blur-sm border border-border/50 p-4 transition-all hover:-translate-y-0.5 hover:border-primary/30 hover:shadow-[0_16px_40px_-20px_rgba(62,92,77,0.18)]"
            >
              <div className="text-[11px] uppercase tracking-[0.18em] font-medium text-primary mb-2">
                {item.n}
              </div>
              <div className="text-base font-medium text-foreground">
                {item.label}
              </div>
            </div>
          ))}
        </div>
      </aside>

      {/* Right side - Auth Form */}
      <div className="flex-1 flex items-center justify-center px-5 py-8 sm:p-6 md:p-8 lg:p-12 pb-safe relative z-10">
        <div className="w-full max-w-md">
          {/* Mobile header */}
          <div className="flex items-center justify-between mb-6 sm:mb-8 lg:hidden">
            <Logo size="sm" />
            <button
              onClick={() => navigate("/")}
              className="inline-flex items-center gap-2 rounded-full bg-secondary/80 px-4 py-2 text-sm font-medium text-foreground hover:bg-foreground hover:text-background transition-colors min-h-[44px]"
            >
              <ArrowLeft className="h-4 w-4" />
              Back
            </button>
          </div>

          {/* Card - rounded glass */}
          <Card className="w-full rounded-3xl bg-card/90 backdrop-blur-md border border-border/50 shadow-[0_40px_80px_-30px_rgba(21,34,27,0.18)] p-6 sm:p-9">
          <CardHeader className="space-y-4 text-center items-center px-0 pt-0 pb-0">
            <div className="space-y-3 sm:space-y-4">
              <CardTitle className="text-[32px] leading-[1.05] sm:text-[40px] md:text-[44px] font-medium text-foreground tracking-[-0.02em]">
                {accountRecoveryPending
                  ? "Account found."
                  : (emailVerificationPending
                    ? "Check your inbox."
                    : (isSignUp ? <>Begin your <span className="italic font-light text-foreground/80">subscription.</span></> : <>Welcome <span className="italic font-light text-foreground/80">back.</span></>))}
              </CardTitle>
              <CardDescription className="text-[15px] sm:text-base text-foreground/65 leading-relaxed px-2">
                {accountRecoveryPending
                  ? `We found an existing account with ${email}`
                  : (emailVerificationPending
                    ? `We've sent a verification link to ${email}`
                    : (isSignUp
                      ? "Start collecting anonymous feedback in two minutes — no card, no friction."
                      : "Sign in to continue to your dashboard."))}
              </CardDescription>
            </div>
          </CardHeader>

          <CardContent className="px-0 pb-0 pt-6 sm:pt-8">
            {/* Google Sign-in - PRIMARY action */}
            {!accountRecoveryPending && !emailVerificationPending && (
              <div className="mb-6 sm:mb-8">
                <button
                  type="button"
                  onClick={async () => {
                    setLoading(true);
                    try {
                      const { error } = await supabase.auth.signInWithOAuth({
                        provider: 'google',
                        options: {
                          redirectTo: `${window.location.origin}/dashboard`,
                        },
                      });
                      if (error) throw error;
                    } catch (error: unknown) {
                      hapticError();
                      const errorMessage = error instanceof Error ? error.message : "Google sign-in failed";
                      toast({
                        title: "Sign-in failed",
                        description: errorMessage,
                        variant: "destructive",
                      });
                      setLoading(false);
                    }
                  }}
                  disabled={loading}
                  className="w-full flex items-center justify-center gap-3 min-h-[52px] px-4 rounded-full border border-foreground/15 bg-background hover:bg-foreground hover:text-background hover:border-foreground transition-all duration-300 text-sm font-medium text-foreground disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
                >
                  {loading ? (
                    <Loader2 className="h-5 w-5 animate-spin" />
                  ) : (
                    <svg className="h-5 w-5" viewBox="0 0 24 24">
                      <path
                        fill="#4285F4"
                        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                      />
                      <path
                        fill="#34A853"
                        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                      />
                      <path
                        fill="#FBBC05"
                        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                      />
                      <path
                        fill="#EA4335"
                        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                      />
                    </svg>
                  )}
                  <span>Continue with Google</span>
                </button>
                <p className="text-center text-xs font-medium text-foreground/55 mt-3">
                  No password needed · Fastest signup
                </p>

                <div className="relative my-6 sm:my-8">
                  <div className="absolute inset-0 flex items-center">
                    <span className="w-full border-t border-foreground/10" />
                  </div>
                  <div className="relative flex justify-center">
                    <span className="bg-card px-4 text-xs font-medium text-foreground/55">Or use email</span>
                  </div>
                </div>
              </div>
            )}
            {accountRecoveryPending ? (
              <div className="space-y-6 text-center py-4">
                <div className="flex justify-center">
                  <div className="relative">
                    <Mail className="h-16 w-16 text-primary animate-pulse" />
                    <div className="absolute -top-1 -right-1 bg-primary text-primary-foreground rounded-full p-1">
                      <Check className="h-4 w-4" />
                    </div>
                  </div>
                </div>

                <div className="space-y-3">
                  <p className="text-sm text-muted-foreground">
                    We've sent a password reset link to your email. Click the link to set a new password and sign in.
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Didn't receive it? Check your spam folder or request a new one.
                  </p>
                </div>

                <div className="space-y-3">
                  <button
                    type="button"
                    onClick={handleResendPasswordReset}
                    disabled={resendCooldown > 0}
                    className="w-full min-h-[52px] inline-flex items-center justify-center gap-2 rounded-full bg-primary text-primary-foreground text-sm font-medium hover:shadow-lg hover:-translate-y-0.5 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-md"
                  >
                    {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : "Resend password reset email"}
                  </button>

                  <button
                    type="button"
                    onClick={() => {
                      setAccountRecoveryPending(false);
                      setIsSignUp(false);
                      navigate('/auth?mode=signin', { replace: true });
                    }}
                    className="w-full min-h-[52px] inline-flex items-center justify-center gap-2 rounded-full border border-foreground/15 bg-background text-foreground text-sm font-medium hover:bg-foreground hover:text-background hover:border-foreground transition-all"
                  >
                    <ArrowLeft className="h-4 w-4" />
                    Back to sign in
                  </button>
                </div>
              </div>
            ) : emailVerificationPending ? (
              <div className="space-y-6 text-center py-4">
                <div className="flex justify-center">
                  <div className="relative">
                    <Mail className="h-16 w-16 text-primary animate-pulse" />
                    <div className="absolute -top-1 -right-1 bg-primary text-primary-foreground rounded-full p-1">
                      <Check className="h-4 w-4" />
                    </div>
                  </div>
                </div>

                <div className="space-y-3">
                  <p className="text-sm text-muted-foreground">
                    We've sent a verification link to <span className="font-medium text-foreground">{email}</span>
                  </p>
                  <p className="text-sm text-muted-foreground">
                    Click the link in your email to verify your account and get started.
                  </p>
                </div>

                {/* Spam folder warning */}
                <div className="rounded-2xl bg-accent/60 p-4">
                  <div className="flex items-start gap-3 text-left">
                    <AlertCircle className="h-5 w-5 text-primary shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-foreground">
                        Check your spam folder
                      </p>
                      <p className="text-sm text-foreground/65 mt-1 leading-relaxed">
                        Our emails sometimes land in spam or promotions. Please check there if you don't see it in your inbox.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="space-y-3">
                  <button
                    type="button"
                    onClick={handleResendVerification}
                    disabled={resendCooldown > 0}
                    className="w-full min-h-[52px] inline-flex items-center justify-center gap-2 rounded-full border border-foreground/15 bg-background text-foreground text-sm font-medium hover:bg-foreground hover:text-background hover:border-foreground transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : "Resend verification email"}
                  </button>

                  <button
                    type="button"
                    onClick={() => {
                      setEmailVerificationPending(false);
                      setIsSignUp(false);
                      navigate('/auth?mode=signin', { replace: true });
                    }}
                    className="w-full min-h-[44px] inline-flex items-center justify-center gap-2 text-sm font-medium text-foreground/65 hover:text-foreground transition-colors"
                  >
                    <ArrowLeft className="h-4 w-4" />
                    Back to sign in
                  </button>
                </div>
              </div>
            ) : (
            <>
            <form onSubmit={handleAuth} className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-sm font-medium text-foreground">Email</Label>
                <div className="relative">
                  <Input
                    id="email"
                    type="email"
                    inputMode="email"
                    autoComplete="email"
                    autoCapitalize="off"
                    autoCorrect="off"
                    placeholder="you@company.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="min-h-[52px] text-base rounded-2xl border-foreground/15 bg-background pr-10 focus:border-primary focus:ring-2 focus:ring-primary/20"
                    disabled={loading}
                    required
                    aria-invalid={!!errors.email}
                    aria-describedby={errors.email ? "email-error" : undefined}
                  />
                  {isEmailValid && email.length > 0 && (
                    <CheckCircle2 className="absolute right-3 top-1/2 -translate-y-1/2 h-5 w-5 text-primary animate-in fade-in zoom-in duration-200" />
                  )}
                </div>
                {errors.email && (
                  <p id="email-error" className="text-sm text-destructive" role="alert">
                    {errors.email}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="password" className="text-sm font-medium text-foreground">Password</Label>
                  {!isSignUp && (
                    <button
                      type="button"
                      onClick={() => setForgotPasswordOpen(true)}
                      className="text-sm font-medium text-primary hover:text-primary-dark transition-colors min-h-[44px] flex items-center"
                    >
                      Forgot password?
                    </button>
                  )}
                </div>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="min-h-[52px] text-base rounded-2xl border-foreground/15 bg-background pr-12 focus:border-primary focus:ring-2 focus:ring-primary/20"
                    disabled={loading}
                    required
                    minLength={6}
                    autoComplete={isSignUp ? "new-password" : "current-password"}
                    aria-invalid={!!errors.password}
                    aria-describedby={errors.password ? "password-error" : undefined}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-foreground/50 hover:text-foreground transition-colors w-10 h-10 flex items-center justify-center rounded-full"
                    aria-label={showPassword ? "Hide password" : "Show password"}
                  >
                    {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
                {errors.password && (
                  <p id="password-error" className="text-sm text-destructive" role="alert">
                    {errors.password}
                  </p>
                )}
                {isSignUp && password && (
                  <div className="lg:block">
                    <PasswordStrengthIndicator password={password} />
                  </div>
                )}
              </div>

              {!isSignUp && (
                <div className="flex items-center space-x-2 py-1">
                  <Checkbox
                    id="rememberMe"
                    checked={rememberMe}
                    onCheckedChange={(checked) => setRememberMe(checked === true)}
                    disabled={loading}
                    className="h-5 w-5 rounded-md border-foreground/30"
                  />
                  <Label 
                    htmlFor="rememberMe" 
                    className="text-sm text-foreground/70 cursor-pointer select-none min-h-[44px] flex items-center"
                  >
                    Remember me for 30 days
                  </Label>
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full min-h-[56px] inline-flex items-center justify-center gap-3 rounded-full bg-primary text-primary-foreground text-sm font-medium hover:shadow-[0_12px_36px_rgba(62,92,77,0.36)] hover:-translate-y-0.5 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed shadow-[0_8px_24px_rgba(62,92,77,0.25)]"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    {isSignUp ? "Creating account..." : "Signing in..."}
                  </>
                ) : (
                  isSignUp ? "Create account" : "Sign in"
                )}
              </button>

              {isSignUp && (
                <>
                  {/* Trust signals */}
                  <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-2 pt-3 text-xs font-medium text-foreground/60">
                    <span className="inline-flex items-center gap-1.5">
                      <Check className="h-3.5 w-3.5 text-primary" strokeWidth={2.5} />
                      No card needed
                    </span>
                    <span className="hidden sm:inline-block h-1 w-1 rounded-full bg-foreground/20" />
                    <span className="inline-flex items-center gap-1.5">
                      <Check className="h-3.5 w-3.5 text-primary" strokeWidth={2.5} />
                      Cancel anytime
                    </span>
                  </div>

                  <p className="text-xs text-center text-foreground/50 pt-1 leading-relaxed">
                    By signing up, you agree to our{" "}
                    <Link to="/terms-of-service" className="text-primary hover:text-primary-dark underline underline-offset-2">
                      Terms
                    </Link>
                    {" "}and{" "}
                    <Link to="/privacy-policy" className="text-primary hover:text-primary-dark underline underline-offset-2">
                      Privacy Policy
                    </Link>
                  </p>
                </>
              )}
            </form>

            {/* Toggle mode */}
            <div className="mt-8 pt-6 border-t border-foreground/10">
              <button
                type="button"
                onClick={toggleMode}
                className="block w-full text-center text-sm text-foreground/65 hover:text-foreground transition-colors min-h-[44px]"
                disabled={loading}
              >
                {isSignUp
                  ? <>Already have an account? <span className="text-primary font-medium ml-1">Sign in</span></>
                  : <>New to SendFeedback? <span className="text-primary font-medium ml-1">Create account</span></>}
              </button>
            </div>
            </>
            )}
          </CardContent>
        </Card>
        </div>
      </div>


      {/* Forgot Password Dialog */}
      <ForgotPasswordDialog 
        open={forgotPasswordOpen} 
        onOpenChange={setForgotPasswordOpen}
        currentMode={isSignUp ? 'signup' : 'signin'}
      />
    </div>
  );
};

export default Auth;
